import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '@/lib/api';
import { useAuth } from '@/context/AuthContext';
import { GlassCard } from '@/components/GlassCard';
import { Logo } from '@/components/Logo';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Check, ArrowLeft, Sparkles, Building2, Crown, Phone, CreditCard, Loader2 } from 'lucide-react';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';
import { Seo } from '@/components/Seo';

export default function PricingPage() {
  const nav = useNavigate();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [cardLoading, setCardLoading] = useState(null);

  useEffect(() => {
    api.get('/plans').then((r) => setData(r.data));
  }, []);

  const handlePick = (planId) => {
    if (!user) {
      nav('/auth/signup');
      return;
    }
    if (user.role === 'venue_owner' || user.role === 'admin') {
      nav('/profile/subscription');
    } else {
      nav('/profile/claim-venue');
    }
  };

  const payByCard = async (planId) => {
    if (!user) {
      nav('/auth/signup');
      return;
    }
    setCardLoading(planId);
    try {
      const { data: mine } = await api.get('/subscriptions/me');
      const venue = mine?.[0]?.venue;
      if (!venue) {
        toast.info('Claim your venue first, then you can pay by card.');
        nav('/profile/claim-venue');
        return;
      }
      const { data: session } = await api.post('/payments/stripe/checkout', {
        venue_id: venue.id,
        plan: planId,
        origin_url: window.location.origin,
      });
      window.location.href = session.checkout_url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not start card payment');
      setCardLoading(null);
    }
  };

  return (
    <div className="relative min-h-screen th-hero-radial pb-28">
      <Seo
        title="Venue Partner Pricing — Local, Regional & Enterprise Plans | Thirrsty.com"
        description="Get your bar, club, lounge, coffee shop, vineyard, brewery, or frat party on Thirrsty.com. Local $99/mo, Regional $499–$999/mo, or a custom Enterprise contract. No setup fees, cancel anytime."
        path="/pricing"
      />
      <header className="sticky top-0 z-30 bg-[#07070A]/85 backdrop-blur-xl border-b border-white/5">
        <div className="mx-auto flex max-w-3xl items-center gap-2 px-4 py-3">
          <Button variant="ghost" size="icon" onClick={() => nav(-1)} className="text-white hover:bg-white/10" data-testid="pricing-back-button">
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <h1 className="font-display text-lg font-semibold" style={{ fontFamily: 'Space Grotesk' }}>For Venue Partners</h1>
          <div className="ml-auto"><Logo size={96} withText={false} asDownloadTrigger /></div>
        </div>
      </header>

      <main className="mx-auto max-w-3xl space-y-5 px-4 py-6">
        <div className="text-center">
          <p className="font-display text-xs uppercase tracking-[0.25em] text-[#F6B24A]" style={{ fontFamily: 'Space Grotesk' }}>List on Thirrsty</p>
          <h2 className="mt-2 font-display text-3xl sm:text-4xl font-semibold text-white" style={{ fontFamily: 'Space Grotesk' }}>Bring your venue to the nightlife crowd.</h2>
          <p className="mt-2 text-sm text-white/70">Three packages for every size. No setup fees. Cancel anytime.</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {data?.plans.map((p) => {
            const isEnterprise = p.id === 'national';
            const isRegional = p.id === 'regional';
            const accent = isEnterprise || isRegional;
            return (
              <GlassCard
                key={p.id}
                className={cn(
                  'p-6 relative overflow-hidden',
                  isEnterprise && 'border-[#2DE2E6]/40 shadow-[0_0_50px_rgba(45,226,230,0.10)]'
                )}
                data-testid={`pricing-card-${p.id}`}
              >
                {isEnterprise && (
                  <div className="absolute right-3 top-3">
                    <Badge className="bg-[#2DE2E6]/15 text-[#2DE2E6] border-[#2DE2E6]/30 inline-flex items-center gap-1">
                      <Crown className="h-3 w-3" /> Featured
                    </Badge>
                  </div>
                )}
                <div className="flex items-center gap-3">
                  <div className={cn(
                    'flex h-11 w-11 items-center justify-center rounded-xl',
                    accent ? 'bg-[#2DE2E6]/15 text-[#2DE2E6]' : 'bg-[#F6B24A]/15 text-[#F6B24A]'
                  )}>
                    {isEnterprise ? <Crown className="h-5 w-5" /> : <Building2 className="h-5 w-5" />}
                  </div>
                  <div>
                    <p className="font-display text-2xl font-semibold text-white" style={{ fontFamily: 'Space Grotesk' }} data-testid={`pricing-card-${p.id}-name`}>{p.name}</p>
                    <p className="text-xs text-white/60">{p.tagline}</p>
                  </div>
                </div>
                <div className="mt-4 flex items-baseline gap-2">
                  {isEnterprise ? (
                    <span className="font-display text-3xl font-semibold text-white" style={{ fontFamily: 'Space Grotesk' }} data-testid={`pricing-card-${p.id}-price`}>Custom Contract</span>
                  ) : isRegional ? (
                    <>
                      <span className="font-display text-3xl font-semibold text-white" style={{ fontFamily: 'Space Grotesk' }} data-testid={`pricing-card-${p.id}-price`}>$499–$999</span>
                      <span className="text-sm text-white/60">/ month</span>
                    </>
                  ) : (
                    <>
                      <span className="font-display text-4xl font-semibold text-white" style={{ fontFamily: 'Space Grotesk' }} data-testid={`pricing-card-${p.id}-price`}>${p.price_usd.toLocaleString()}</span>
                      <span className="text-sm text-white/60">/ month</span>
                    </>
                  )}
                </div>
                {p.trial_label && (
                  <p className="mt-1 inline-flex items-center gap-1 text-xs font-semibold text-[#F6B24A]" data-testid={`pricing-card-${p.id}-trial`}>
                    <Sparkles className="h-3 w-3" /> {p.trial_label}
                  </p>
                )}
                <ul className="mt-5 space-y-2">
                  {p.perks.map((perk, i) => (
                    <li key={i} className="flex items-start gap-2 text-sm text-white/85">
                      <Check className={cn('mt-0.5 h-4 w-4 shrink-0', accent ? 'text-[#2DE2E6]' : 'text-[#F6B24A]')} />
                      <span>{perk}</span>
                    </li>
                  ))}
                </ul>
                <Button
                  onClick={() => (isEnterprise ? nav('/contact') : handlePick(p.id))}
                  className={cn(
                    'mt-6 w-full h-11 rounded-xl font-semibold',
                    accent
                      ? 'bg-[#2DE2E6] text-[#06262A] hover:bg-[#5BE9ED]'
                      : 'bg-[#F6B24A] text-[#1A1208] hover:bg-[#FFC065]'
                  )}
                  data-testid={`pricing-card-${p.id}-cta`}
                >
                  {isEnterprise ? 'Contact Sales' : user ? (p.id === 'local' && p.trial_days ? 'Start free month' : 'Get started') : 'Sign up to get started'}
                </Button>
              </GlassCard>
            );
          })}
        </div>

        <GlassCard className="p-5">
          <div className="flex items-center gap-2 text-[#F6B24A]">
            <Sparkles className="h-4 w-4" />
            <p className="text-xs uppercase tracking-wider font-semibold">How payment works</p>
          </div>

          <div className="mt-3 rounded-xl border border-[#2DE2E6]/30 bg-[#2DE2E6]/[0.07] p-4" data-testid="pricing-card-payment-section">
            <div className="flex items-center gap-2 text-[#2DE2E6]">
              <CreditCard className="h-4 w-4" />
              <p className="text-sm font-semibold text-white">Pay by card <span className="ml-1 rounded-full bg-[#2DE2E6]/15 px-2 py-0.5 text-[10px] uppercase tracking-wider text-[#2DE2E6]">Instant</span></p>
            </div>
            <p className="mt-1.5 text-sm text-white/75">
              Secure checkout powered by <span className="font-semibold text-white">Stripe</span>. Your plan activates immediately after payment.
            </p>
            <div className="mt-3 flex flex-col sm:flex-row gap-2">
              <Button
                onClick={() => payByCard('local')}
                disabled={!!cardLoading}
                className="flex-1 h-10 rounded-xl bg-[#F6B24A] text-[#1A1208] hover:bg-[#FFC065] font-semibold"
                data-testid="pricing-pay-card-local-button"
              >
                {cardLoading === 'local' ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <CreditCard className="mr-2 h-4 w-4" />}
                Local — $99/mo
              </Button>
              <Button
                onClick={() => payByCard('regional')}
                disabled={!!cardLoading}
                className="flex-1 h-10 rounded-xl bg-[#2DE2E6] text-[#06262A] hover:bg-[#5BE9ED] font-semibold"
                data-testid="pricing-pay-card-regional-button"
              >
                {cardLoading === 'regional' ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <CreditCard className="mr-2 h-4 w-4" />}
                Regional — from $499/mo
              </Button>
            </div>
            <p className="mt-2 text-xs text-white/50">You'll be redirected to a secure Stripe checkout page. Enterprise/National Partner plans are custom contracts — <button type="button" onClick={() => nav('/contact')} className="text-[#2DE2E6] hover:underline" data-testid="pricing-enterprise-contact-link">contact sales</button>.</p>
          </div>

          <p className="mt-4 text-sm text-white/80">
            Prefer <span className="font-semibold text-white">Zelle</span>? Fee-free bank transfer, verified by our team.
          </p>
          <ol className="mt-3 space-y-1 text-sm text-white/80 list-decimal pl-5">
            {data?.zelle?.instructions.map((step, i) => (
              <li key={i}>{step}</li>
            ))}
          </ol>
          <div className="mt-3 rounded-xl border border-[#F6B24A]/30 bg-[#F6B24A]/10 p-3">
            <p className="text-xs text-[#F6B24A] uppercase tracking-wider">Zelle recipient</p>
            <a href="tel:+14105047466" className="mt-1 inline-flex items-center gap-2 text-white font-mono text-base" data-testid="pricing-zelle-recipient">
              <Phone className="h-4 w-4" />
              {data?.zelle?.recipient}
            </a>
            <p className="text-xs text-white/60">Recipient: {data?.zelle?.recipient_name}</p>
          </div>
        </GlassCard>

        <GlassCard className="p-4 text-center text-sm text-white/70">
          Already a partner?{' '}
          <button onClick={() => nav('/profile/subscription')} className="text-[#F6B24A] hover:underline" data-testid="pricing-existing-partner-link">View your subscription</button>
          {' '}or{' '}
          <button onClick={() => nav('/contact')} className="text-[#F6B24A] hover:underline">Contact sales</button>
        </GlassCard>
      </main>
    </div>
  );
}
